import React, { useContext } from 'react'
import {
  PropertyCard,
  PropertyCardTranslationProps,
} from '@awaze/design-system.compositions.property-card-composite'
import { formatter } from 'src/containers/SearchResults/propertyFormatter.util'
import { PropertyListSkeleton } from '@awaze/design-system.compositions.property-list'
import { CommonSite } from '@awaze/mfe-common.types-and-mocks.site-search-service'
import { trackClientSideOptimizelyEvent } from 'src/utils/optimizely/trackOptimizelyEvent'
import { OPTIMIZELY_EVENTS, RESULTS_PAGE_SIZE } from 'src/constants'
import { useOptimizelyDecisionsContext } from 'src/context/optimizely-decisions-context'
import { dataLayerErrorHandler } from 'src/utils/dataLayerErrorHandler'
import type { Deposits } from '@awaze/mfe-common.configs.brand'
import { StyledPropertyList } from './property-list.style'

import {
  DataLayerEventType,
  DataLayerFactory,
  TmsContext,
} from '@awaze/mfe-common.contexts.tms'
import { Brand } from '@awaze/design-system.components.awaze-theme-provider'
import {
  AccommodationType,
  SearchCriteria,
  CommonSearchResult,
} from '@awaze/mfe-common.types-and-mocks.site-search-service'
import { ImagesItem } from '@awaze/mfe-common.types-and-mocks.site-search-service/types/properties.types'
import { Image } from '@awaze/design-system.compositions.property-list/dist/property-list.types'
import { removeFloorplanImagesFromImageArray } from 'src/containers/SearchResults/helpers'

export interface PropertyListCompositeProps {
  properties: CommonSite[]
  translations: PropertyCardTranslationProps
  deposits?: Deposits
  config: {
    brand: Brand
    searchCriteria: SearchCriteria
    searchData: CommonSearchResult
    searchParameters: string
    siteSearch: boolean
    dateToday: Date
    accommodationType: AccommodationType
    pdpUrlSegment: string
    locale: string
  }
  onCarouselInteractFactory: (
    images: Array<Image | ImagesItem>,
    displayName: string,
  ) => (
    event: 'navigate',
    index?: number,
    nextOrPrevious?: 'next' | 'previous',
  ) => void
}

export const PropertyListComposite = ({
  properties,
  translations,
  deposits,
  config,
  onCarouselInteractFactory,
}: PropertyListCompositeProps): React.ReactNode => {
  const tms = useContext(TmsContext)
  const optiContext = useOptimizelyDecisionsContext()
  const searchCriteria = config?.searchCriteria
  const searchData = config.searchData
  const searchParameters = new URLSearchParams(config?.searchParameters)
  const datedSearch = searchParameters.has('start')

  const onPropertyLinkClick = ({ propertyCode }: { propertyCode: string }) => {
    new DataLayerFactory()
      .getEvent(DataLayerEventType.PropertyClick)
      .update(
        tms,
        { propertyCode, searchCriteria, searchData },
        dataLayerErrorHandler,
      )
    trackClientSideOptimizelyEvent(
      OPTIMIZELY_EVENTS.PDP_CLICK_THROUGH,
      optiContext,
    )
  }

  return (
    <StyledPropertyList aria-label="properties">
      {!properties.length && (
        <PropertyListSkeleton resultsPageSize={RESULTS_PAGE_SIZE} />
      )}

      {properties.length > 0 &&
        properties.map((property: CommonSite) => {
          const propertyImagesWithoutFloorplans = property.images
            ? removeFloorplanImagesFromImageArray(property.images)
            : []

          return (
            <PropertyCard
              key={property.code}
              {...formatter({
                property: property,
                brand: config.brand,
                deposits: deposits,
                searchParams: searchParameters,
                datedSearch: datedSearch,
                pdpUrlSegment: config.pdpUrlSegment,
              })}
              onPropertyLinkClick={() =>
                onPropertyLinkClick({ propertyCode: property.code })
              }
              translations={translations}
              siteSearch={config.siteSearch}
              locale={config.locale ? config.locale : 'en-GB'}
              onCarouselInteractCallback={onCarouselInteractFactory(
                propertyImagesWithoutFloorplans,
                property.displayName ?? '',
              )}
            />
          )
        })}
    </StyledPropertyList>
  )
}
